import * as R from "ramda";
import * as Dfns from "date-fns/fp";
import {
  DATE_GROUPING_OPTIONS,
  METRIC_TO_PRETTY_NAME,
  DateGroupingOption,
} from "./homePageConstants";
import {
  formatMoney,
  formatNumber,
  formatNumberAsInt,
} from "@blisspointmedia/bpm-types/dist/formatUtils";
import { VOLUME_FORMATTER } from "@blisspointmedia/bpm-types/dist/metricFormatUtils";
import { PerformanceData } from "@blisspointmedia/bpm-types/dist/MetricsTable";

/**
 * Return the date grouping options that are relevant based on the provided date range.
 * For example, if the date range spans 3 weeks we don't want to provide the option to group by
 * month, quarter, or year.
 */
export const getDateGroupingOptions = (start: string, end: string): DateGroupingOption[] => {
  const DATE_GROUPING_TO_DAYS = {
    Day: 1,
    Week: 7,
    Month: 31,
    Quarter: 89,
    Year: 366,
  };

  const daysDiff = Dfns.differenceInDays(Dfns.parseISO(start), Dfns.parseISO(end));
  return DATE_GROUPING_OPTIONS.filter(option => daysDiff > DATE_GROUPING_TO_DAYS[option.value]);
};

// TODO: Move these to a more appropriate location
// Metric formatters
export const ROAS_FORMATTER = (value: number): string =>
  value > 0 && value < 0.01 ? "<0.01" : formatNumber(value, 2);
export const CPX_FORMATTER = (value: number): string =>
  value > 0 && value < 0.01 ? "<$0.01" : formatMoney(value, value < 15 ? 2 : 0);
export const CPM_FORMATTER = (value: number): string => formatMoney(value, 2);
export const IMP_FORMATTER = (value: number): string => formatNumberAsInt(value);
export const SPEND_FORMATTER = (value: number): string =>
  value > 0 && value < 1 ? "<$1" : formatMoney(value, 0);
export const REVENUE_FORMATTER = (value: number): string =>
  value > 0 && value < 1 ? "<$1" : formatMoney(value, 0);
export const RELATIVE_FORMATTER = (value: number): string =>
  `${Math.round(value).toLocaleString()}%`;
export const PERCENTAGE_FORMATTER = (value: number, decimals = 0): string =>
  `${formatNumber(value * 100, decimals)}%`;
export const CTR_FORMATTER = (value: number): string =>
  `${formatNumber(value * 100, 2).toLocaleString()}%`;

export const SPEND_IN_THOUSANDS_FORMATTER = (value: number): string =>
  value > 0 && value < 1 ? "<$1" : formatMoney(value / 1000, 0);
export const IMP_IN_THOUSANDS_FORMATTER = (value: number): string =>
  formatNumberAsInt(value / 1000);
// formatValue receives parameters from metricsTableUtils.ts which we are utilizing here to determine special formatting for Streaming Video.
const CLICKS_FORMATTER = (value: number, _: number, row: PerformanceData): string => {
  if (R.path(["dimensions", "Channel"], row) === "Streaming Video") {
    return "--";
  }
  return value > 0 && value < 1 ? "<1" : formatNumberAsInt(value);
};

export const METRIC_TO_FORMATTER = {
  acos: PERCENTAGE_FORMATTER,
  aov: REVENUE_FORMATTER,
  clicks: CLICKS_FORMATTER,
  clicksConversionRate: (value: number): string => PERCENTAGE_FORMATTER(value, 2),
  cpc: CPM_FORMATTER,
  cpm: CPM_FORMATTER,
  cpx: CPX_FORMATTER,
  ctr: CTR_FORMATTER,
  impressionShare: (value: number): string => PERCENTAGE_FORMATTER(value, 2),
  imps: IMP_FORMATTER,
  impsConversionRate: (value: number): string => PERCENTAGE_FORMATTER(value, 2),
  percentage: PERCENTAGE_FORMATTER,
  relative: RELATIVE_FORMATTER,
  revenue: REVENUE_FORMATTER,
  roas: ROAS_FORMATTER,
  spend: SPEND_FORMATTER,
  topImpressionShare: (value: number): string => PERCENTAGE_FORMATTER(value, 2),
  volume: VOLUME_FORMATTER,
};

export const metricFormatter = (metric: string): ((value: number) => string) => {
  if (METRIC_TO_FORMATTER[metric]) {
    return METRIC_TO_FORMATTER[metric];
  } else {
    return METRIC_TO_FORMATTER.volume;
  }
};

export const getPrettyMetricName = (metric: string): string => {
  return METRIC_TO_PRETTY_NAME[metric] || metric;
};

/**
 * Used to get the company name from a KPI. Pretty much just relevant for "global" companies.
 * E.g. company is instacartglobal but KPI is instacart_visits_filtered, so we use instacart as the company.
 */
export const getBrandFromKpi = (kpi: string): string | undefined => {
  let kpiCompanyMatch = kpi.match(/^([^_]+)_/);
  if (kpiCompanyMatch && kpiCompanyMatch.length >= 2) {
    return kpiCompanyMatch[1];
  }
  return undefined;
};
